import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LaunchDialogService } from '@spartacus/storefront';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { ProductService } from '../../../../core/catalog';
import { BaseAbstractModalComponent, ModalCloseReason } from '../../../../core/modal';
import { CartType, Product } from '../../../../core/model';
import { PrincipalConfigurationService } from '../../../../core/user';
import { DropdownItem } from '../../../../shared/components/reactive-forms-input/reactive-form-dropdown-field';
import { ActiveCartFacade } from '../../../cart/base';

interface Option {
  label: string;
  value: string;
}
@Component({
  selector: 'py-sample-order-modal-modal',
  templateUrl: './sample-order-modal.component.html',
  styleUrls: ['./sample-order-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SampleOrderModalComponent extends BaseAbstractModalComponent implements OnInit, OnDestroy {
  private sampleCartType = CartType.sample;
  private subscriptions = new Subscription();

  sampleProductForm: UntypedFormGroup = new UntypedFormGroup({
    grammage: new UntypedFormControl(0, [Validators.required]),
    color: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    quantity: new UntypedFormControl({ value: 0, disabled: true }, [Validators.required, Validators.min(1)]),
  });

  product: Product;
  maxA4SampleProductsPerOrder: number;
  maxA4SampleSheetsPerProduct: number;
  hasColors: boolean = true;

  product$: Observable<Product>;
  sampleProductColors$: Observable<Option[]>;
  productsLimit$: Observable<number>;
  disableButton$: Observable<boolean>;
  grammageDropDownItems$: Observable<DropdownItem[]>;

  constructor(
    private productService: ProductService,
    private activeCartService: ActiveCartFacade,
    private principalConfigurationService: PrincipalConfigurationService,
    protected launchDialogService: LaunchDialogService,
    protected el: ElementRef,
    protected renderer: Renderer2
  ) {
    super(el, launchDialogService, renderer);
  }

  private get grammageFormControl(): UntypedFormControl {
    return this.sampleProductForm.get('grammage') as UntypedFormControl;
  }

  get colorFormControl(): UntypedFormControl {
    return this.sampleProductForm.get('color') as UntypedFormControl;
  }

  get quantityFormControl(): UntypedFormControl {
    return this.sampleProductForm.get('quantity') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.product$ = this.launchDialogService.data$.pipe(take(1));

    this.subscriptions.add(this.product$.subscribe((data) => (this.product = data)));

    this.grammageDropDownItems$ = this.product$.pipe(
      map((product: Product) =>
        product.sample?.a4SampleGrammageList?.map((grammage) => ({ label: `${grammage}`, value: `${grammage}` }) as DropdownItem)
      )
    );

    this.sampleProductColors$ = combineLatest([this.product$, this.grammageFormControl.valueChanges]).pipe(
      switchMap(([product, grammage]) => {
        this.colorFormControl.reset();
        return this.productService.getA4SampleColors(product.code, grammage);
      }),
      map((colors) => {
        if (colors.length === 0) {
          this.colorFormControl.clearValidators();
          this.quantityFormControl.updateValueAndValidity();
          this.hasColors = false;
        } else {
          this.colorFormControl.enable();
        }
        return colors.map((color) => ({
          label: `${color.description} (${color.code})`,
          value: color.code,
        }));
      })
    );

    this.disableButton$ = combineLatest([
      this.principalConfigurationService.getValue('maxA4SampleProductsPerOrder'),
      this.activeCartService.getActiveCart(this.sampleCartType),
    ]).pipe(
      map(([maxA4SampleProductsPerOrder, cartEntries]) => {
        this.maxA4SampleProductsPerOrder = maxA4SampleProductsPerOrder?.value as number;
        return cartEntries?.entries?.length >= this.maxA4SampleProductsPerOrder;
      })
    );

    this.productsLimit$ = combineLatest([
      this.principalConfigurationService.getValue('maxA4SampleSheetsPerProduct'),
      this.activeCartService.getActiveCart(this.sampleCartType),
      this.product$,
    ]).pipe(
      map(([maxA4SampleSheetsPerProduct, cartEntries, product]) => {
        if (!cartEntries) {
          return 0;
        }
        this.maxA4SampleSheetsPerProduct = maxA4SampleSheetsPerProduct?.value as number;
        const sampleProductSheetsQuantity: number = cartEntries?.entries
          ?.filter((entry) => entry.a4SampleProduct?.code === product?.code)
          ?.reduce((quantity, entity) => entity.quantity + quantity, 0);

        const limit = sampleProductSheetsQuantity
          ? this.maxA4SampleSheetsPerProduct - sampleProductSheetsQuantity
          : this.maxA4SampleSheetsPerProduct;

        // this block is to enabling the quantity and making its value to 1 when the max is not reached.
        if (limit !== 0) {
          this.quantityFormControl.setValidators([Validators.required, Validators.min(1), Validators.max(limit)]);
          this.quantityFormControl.setValue(1);
          this.quantityFormControl.enable();
          this.quantityFormControl.updateValueAndValidity();
        }
        return limit;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  dismissModal(): void {
    this.closeModal(ModalCloseReason.SAMPLE_ORDER_DISMISSED);
  }

  addSampleEntryToCart(): void {
    this.activeCartService.addA4SampleEntry(
      this.product?.code,
      this.sampleProductForm.get('grammage').value,
      this.sampleProductForm.get('color').value || '',
      this.sampleProductForm.get('quantity').value
    );
    this.closeModal(ModalCloseReason.SAMPLE_ORDER_CONFIRMED);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
